import React, { type ReactElement, type ReactNode } from 'react';

import classnames from 'classnames';

import { type SizeType, useSize } from '../../features/config';
import { type ThemedColors } from '../../types/themed-colors';
import { Small } from '../Typography';

import styles from './Badge.module.scss';

export type BadgeProps = {
    children: ReactNode;
    /**
     * The icon to be shown in the badge.
     */
    icon?: ReactNode;
    /**
     * The number to be shown in the badge.
     */
    number?: number;
    /**
     * The position of the badge relative to the `children` size.
     */
    position?: 'topLeft' | 'topRight' | 'bottomLeft' | 'bottomRight' | 'center';
    /**
     * The color of the badge background.
     * - When `number` is provided, the default `color` is `danger`.
     * - When `icon` is provided, the default `color` is `none`.
     */
    color?: ThemedColors;
    /** The offset of the badge relative to the `position` prop.
     * The format is `[horizontal, vertical]`.
     * It can be a number or a string with a unit including nevative values.
     */
    offset?: [number | string, number | string];
    /**
     * Whether the badge should fill the whole space of its `children`.
     * Usually used with `position="center"`.
     */
    fill?: boolean;
    /**
     *  Whether the badge should be shown only when the `children` is hovered.
     */
    showOnHover?: boolean;
    /**
     * The size of the badge item container.
     */
    size?: SizeType;
};

const parseOffset = (value: string | number) => parseInt(value as string, 10);

export const Badge = ({
    children,
    number,
    icon,
    position = 'topRight',
    color,
    offset = [0, 0],
    showOnHover = false,
    fill = false,
    size: sizeProp
}: BadgeProps): ReactElement => {
    const hasNumber = number !== undefined && number !== null;
    const defaultColor: ThemedColors = hasNumber ? 'danger' : 'inherit';
    const styleColor: ThemedColors = color ?? defaultColor;
    const size = useSize(sizeProp, 'medium', 'Badge');

    const getOffsetStyle = (): React.CSSProperties => {
        if (!offset) {
            return {};
        }
        const offsetStyle: React.CSSProperties = {};
        if (['topLeft', 'bottomLeft'].includes(position)) {
            offsetStyle.left = parseOffset(offset[0]);
        }
        if (['topRight', 'bottomRight'].includes(position)) {
            offsetStyle.right = parseOffset(offset[0]);
        }
        if (['topLeft', 'topRight'].includes(position)) {
            offsetStyle.top = parseOffset(offset[1]);
        }
        if (['bottomLeft', 'bottomRight'].includes(position)) {
            offsetStyle.bottom = parseOffset(offset[1]);
        }
        return offsetStyle;
    };

    return (
        <span
            className={classnames(styles.badge, styles[position], styles[size], {
                [styles.showOnHover]: showOnHover,
                [styles.fill]: fill
            })}
        >
            {number && number > 0 && (
                <Small
                    className={classnames(styles.badgeItem, styles.number, styles[styleColor])}
                    style={getOffsetStyle()}
                >
                    {number}
                </Small>
            )}
            {icon && (
                <span
                    className={classnames(styles.badgeItem, styles.icon, styles[styleColor])}
                    style={getOffsetStyle()}
                >
                    {icon}
                </span>
            )}
            {children}
        </span>
    );
};
